import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { WfPermissionService } from '../services/wf-permission.service';

/**
 * Functional route guard for the wf-admin module.
 *
 * <p>Reads the {@code requiredRoles} array from the route's {@code data}
 * map. Allows navigation if the user has any of the listed roles or has
 * the {@code wf-admin} wildcard. Otherwise redirects to {@code /} (or to a
 * configured fallback) — by design we redirect rather than show a 403, so
 * the user never sees a "you don't have permission" message for a page
 * that was never meant to appear in their menu in the first place.
 *
 * <h2>Usage in routes</h2>
 *
 * <pre>{@code
 *   {
 *     path: 'admin/wf/bpmn',
 *     component: BpmnListComponent,
 *     canActivate: [wfRoleGuard],
 *     data: { requiredRoles: ['wf-bpmn-editor', 'wf-admin'] }
 *   }
 * }</pre>
 *
 * <p>If {@code requiredRoles} is missing or empty, the guard allows access
 * (consistent with the directive's behavior).
 */
export const wfRoleGuard: CanActivateFn = (route): boolean | UrlTree => {
  const permissions = inject(WfPermissionService);
  const router = inject(Router);

  const required = (route.data?.['requiredRoles'] as string[] | undefined) ?? [];
  if (required.length === 0) return true;

  if (permissions.hasAnyRole(required)) return true;

  console.warn('wfRoleGuard: blocked', {
    path: route.url.map(s => s.path).join('/'),
    required
  });
  return router.createUrlTree(['/']);
};
